import React, { useEffect, useRef, useState } from "react";
import Highlighter from "react-highlight-words";

import { Button, Col, Divider, Input, InputRef, Modal, Row, Space } from "antd";
import { ColumnsType, ColumnType } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons";
import { FilterConfirmProps } from "antd/lib/table/interface";

import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";
import DataTable from "../components/DataTable";
import RegisterCarType from "../components/RegisterCarType";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as String from "../commons/string";
import * as Utils from "../utils/utils";

type DataIndex = keyof Types.CarTypeDto;

function CarTypeList({ modelId }: { modelId: string }) {
    useEffect(() => {
        requestGetCarTypes(modelId);
    }, [modelId]);

    const alert = useAlert();

    const [carTypes, setCarTypes] = useState<Array<Types.CarTypeDto>>();
    const [currentCarType, setcurrentCarType] = useState<Types.CarTypeDto>();

    const [editMode, setEditMode] = useState<string>("none");
    const [openDeleteCarType, setOpenDeleteCarType] = useState(false);

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<Types.CarTypeDto> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        검색
                    </Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        초기화
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        필터
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        닫기
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<Types.CarTypeDto> = [
        {
            align: "center",
            title: String.carTypeId,
            dataIndex: "typeId",
            key: "typeId",
            ...getColumnSearchProps("typeId"),
        },
        {
            align: "center",
            title: String.carTypeName,
            dataIndex: "typeName",
            key: "typeName",
            ...getColumnSearchProps("typeName"),
        },
        {
            align: "center",
            title: String.carTypePrice,
            dataIndex: "price",
            key: "price",
            ...getColumnSearchProps("price"),
        },
        {
            align: "center",
            title: String.colorName,
            dataIndex: "colors",
            key: "colors",
            render: (_, { colors }) => {
                let name = "";
                colors.map((object) => {
                    name = name + object.colorName + " ";
                });
                return colors.length !== 0 ? <span>{name}</span> : <span>-</span>;
            },
        },
        {
            align: "center",
            title: String.optionName,
            dataIndex: "options",
            key: "options",
            render: (_, { options }) => {
                let name = "";
                options.map((object) => {
                    name = name + object.optionName + " ";
                });
                return options.length !== 0 ? <span>{name}</span> : <span>-</span>;
            },
        },
        {
            align: "center",
            title: String.unavailable,
            dataIndex: "unavailable",
            key: "unavailable",
            render: (_, { unavailable }) => {
                return unavailable !== null ? <span>{unavailable.toString()}</span> : <span>-</span>;
            },
        },
        {
            align: "center",
            title: String.carModelName,
            dataIndex: ["model", "modelName"],
            key: "modelName",
            ...getColumnSearchProps("model"),
        },
    ];

    const requestAxiosGetCarTypes = async () => {
        let response = await utilAxiosWithAuth().get(Request.CAR_TYPE_LIST_URL);
        if (modelId !== undefined) {
            response = await utilAxiosWithAuth().get(Request.CAR_TYPE_LIST_URL + "?modelId=" + modelId);
        }

        return response.data;
    };

    const {
        loading: loadingGetCarTypes,
        error: errorGetCarTypes,
        data: resultGetCarTypes,
        execute: requestGetCarTypes,
    } = useAsyncAxios(requestAxiosGetCarTypes);

    useEffect(() => {
        if (resultGetCarTypes === null) return;
        // console.log("resultGetCarTypes", resultGetCarTypes);

        setCarTypes(resultGetCarTypes.carTypes);
    }, [resultGetCarTypes]);

    useEffect(() => {
        if (errorGetCarTypes === null) return;

        //console.log("errorGetUsers", errorGetUsers);
        alert.setAlert(errorGetCarTypes.response.data.code, "Error", errorGetCarTypes.response.data.message);
    }, [errorGetCarTypes]);

    const requestAxiosRemoveCarType = async (carType: Types.CarTypeDto) => {
        const response = await utilAxiosWithAuth().delete(Request.CAR_TYPE_URL + "/" + carType?.typeId);
        return response.data;
    };

    const {
        loading: loadingRemoveCarType,
        error: errorRemoveCarType,
        data: resultRemoveCarType,
        execute: requestRemoveCarTypes,
    } = useAsyncAxios(requestAxiosRemoveCarType);

    useEffect(() => {
        if (resultRemoveCarType === null) return;
        // console.log("resultRemoveCarType", resultRemoveCarType);

        alert.setAlert(AlertType.SUCCESS, "타입 삭제 성공", "타입 삭제를 완료하였습니다.");

        requestGetCarTypes();
    }, [resultRemoveCarType]);

    useEffect(() => {
        if (errorRemoveCarType === null) return;

        alert.setAlert(errorRemoveCarType.response.data.code, "Error", errorRemoveCarType.response.data.message);
    }, [errorGetCarTypes]);

    useEffect(() => {
        requestGetCarTypes();
    }, []);

    const onRow = (value: Types.CarTypeDto) => {
        //console.log("onRow", value);
        setcurrentCarType(value);
    };

    const handleDeleteCarType = () => {
        if (currentCarType !== undefined) setOpenDeleteCarType(true);
    };

    const handleDeleteCarTypeOk = () => {
        setOpenDeleteCarType(false);
        requestRemoveCarTypes(currentCarType);
    };

    const handleDeleteCarTypeCancel = () => {
        setOpenDeleteCarType(false);
    };

    const onRegisterCarType = () => {
        requestGetCarTypes();
        setEditMode("none");
    };

    return (
        <>
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                <Row justify={"space-between"}>
                    <Col span={6}>
                        <Divider orientation="left">
                            <span>타입</span>
                        </Divider>
                    </Col>
                    <Space>
                        <Button
                            onClick={() => {
                                editMode === "add" ? setEditMode("none") : setEditMode("add");
                            }}
                        >
                            {String.userAdd}
                        </Button>
                        <Button
                            onClick={() => {
                                editMode === "update" ? setEditMode("none") : setEditMode("update");
                            }}
                        >
                            {String.userUpdate}
                        </Button>
                        <Button onClick={() => handleDeleteCarType()} danger>
                            {String.userRemove}
                        </Button>
                    </Space>
                </Row>
            </Space>
            <DataTable hasScroll={false} columns={columns} rowKey="typeId" dataSource={carTypes} onRow={onRow} />

            {editMode === "add" && <RegisterCarType editMode={editMode} onRegisterCarType={onRegisterCarType} />}

            {editMode === "update" && currentCarType !== undefined && (
                <RegisterCarType editMode={editMode} carType={currentCarType} onRegisterCarType={onRegisterCarType} />
            )}

            {openDeleteCarType && (
                <Modal open={openDeleteCarType} onOk={handleDeleteCarTypeOk} onCancel={handleDeleteCarTypeCancel}>
                    {currentCarType?.typeName} 삭제하시겠습니까?
                </Modal>
            )}
        </>
    );
}

export default CarTypeList;
